import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CalendarClock,
  Check,
  Compass,
  ExternalLink,
  Mail,
  MapPin,
  MessageCircle,
  Search,
  Settings2,
  Sparkles,
  X,
} from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useGraphToken } from "../auth/useGraphToken";
import { saveChampions } from "../api/champions";
import { useChampionsConfig } from "../hooks/useChampionsConfig";
import { useProfile } from "../hooks/useProfile";
import { useChampionDirectory, type ChampionProfile } from "../hooks/useChampionDirectory";
import {
  DEFAULT_CONFIG,
  type ChampionsConfig,
  type Community,
  type CommunityId,
  type Milestone,
} from "../data/championsDirectory";
import { COMMUNITY_ICONS, Face } from "../components/champions/parts";
import { ChampionAdmin, type SaveState } from "../components/champions/ChampionAdmin";
import { TopContributors } from "../components/champions/TopContributors";
import { ChampionsCup } from "../components/champions/ChampionsCup";
import { PointsGuide } from "../components/champions/PointsGuide";

const teamsChat = (email: string) => `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`;

const unique = (values: string[]) => [...new Set(values)];

const fmtDate = (ymd: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const todayYmd = () => new Date().toISOString().slice(0, 10);

/** A community's milestones: its own plus the ones for every community. */
const timelineFor = (milestones: Milestone[], id: CommunityId) =>
  milestones.filter((m) => !m.community || m.community === id).sort((a, b) => a.date.localeCompare(b.date));

function Timeline({ milestones }: { milestones: Milestone[] }) {
  const today = todayYmd();
  const next = milestones.find((m) => m.date >= today)?.date;
  return (
    <ol className="ch-timeline ch-timeline--stacked">
      {milestones.map((m, i) => {
        const state = m.date < today ? "done" : m.date === next ? "next" : "later";
        return (
          <li key={m.id ?? i} className={`ch-step ch-step--${state}`}>
            <span className="ch-step-dot" aria-hidden>
              {state === "done" ? <Check size={12} /> : null}
            </span>
            <span className="ch-step-date">
              {fmtDate(m.date)}
              {state === "next" ? <span className="ch-step-next">Up next</span> : null}
            </span>
            <span className="ch-step-text">{m.text}</span>
          </li>
        );
      })}
    </ol>
  );
}

function CommunityModal({
  community,
  people,
  milestones,
  highlight,
  me,
  onClose,
}: {
  community: Community;
  people: ChampionProfile[];
  /** This community's timeline. */
  milestones: Milestone[];
  highlight: string | null;
  me: string | null;
  onClose: () => void;
}) {
  const Icon = COMMUNITY_ICONS[community.id];
  const closeRef = useRef<HTMLButtonElement>(null);
  const emails = people.map((p) => p.email).filter(Boolean) as string[];

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (highlight) document.getElementById(`ch-person-${highlight}`)?.scrollIntoView({ block: "center" });
  }, [highlight]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ch-modal ch-t-${community.id}`} role="dialog" aria-modal="true" aria-labelledby="ch-modal-title">
        <div className="ch-modal-head">
          <span className="ch-tile" aria-hidden>
            <Icon size={20} />
          </span>
          <div className="ch-modal-titles">
            <p className="ch-kicker">AI Champions community</p>
            <h2 id="ch-modal-title" className="ch-modal-title">
              {community.name}
            </h2>
            <p className="ch-modal-sub">
              {people.length} champions. Reach out to any of them directly.
            </p>
          </div>
          <button ref={closeRef} type="button" className="ch-close" onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>

        <div className="ch-modal-body">
          <ul className="ch-people">
            {people.map((p) => {
              const id = p.name.replace(/\W+/g, "-").toLowerCase();
              return (
                <li key={p.name} id={`ch-person-${id}`} className={`ch-person${highlight === id ? " is-highlight" : ""}`}>
                  <Face name={p.name} photoUrl={p.photoUrl} size={64} />
                  <div className="ch-person-text">
                    <p className="ch-person-name">
                      {p.name}
                      {me && p.email?.toLowerCase() === me ? <span className="ch-you">You</span> : null}
                    </p>
                    {p.title ? <p className="ch-person-title">{p.title}</p> : null}
                    <p className="ch-person-meta">
                      <span className="ch-area">{p.area}</span>
                      {p.location ? (
                        <span className="ch-place">
                          <MapPin size={12} aria-hidden /> {p.location}
                        </span>
                      ) : null}
                    </p>
                    {p.email ? (
                      <p className="ch-person-actions">
                        <a href={`mailto:${p.email}`} aria-label={`Email ${p.name}`}>
                          <Mail size={13} aria-hidden /> Email
                        </a>
                        <a href={teamsChat(p.email)} target="_blank" rel="noreferrer" aria-label={`Chat with ${p.name} in Teams`}>
                          <MessageCircle size={13} aria-hidden /> Chat
                        </a>
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          {milestones.length ? (
            <div className="ch-modal-timeline">
              <p className="ch-label">Timeline</p>
              <Timeline milestones={milestones} />
            </div>
          ) : null}

          <div className="ch-examples">
            <p className="ch-label">What AI could look like in this work</p>
            <ul>
              {community.examples.map((e) => (
                <li key={e}>
                  <Check size={14} aria-hidden /> {e}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {emails.length ? (
          <div className="ch-modal-foot">
            <span>Not sure who to ask? Reach the whole community at once.</span>
            <a
              className="ch-btn ch-btn--primary"
              href={`mailto:${emails.join(",")}?subject=${encodeURIComponent("AI Champions question")}`}
            >
              <Mail size={15} aria-hidden /> Email all {emails.length}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The redesigned AI Champions page (admins and AI Champions while in preview): who the
 * champions are, grouped into their six communities, with each community's
 * people in a popup.
 */
export function ChampionsNewPage() {
  const remote = useChampionsConfig();
  const getToken = useGraphToken();
  // Admin edits show on the page straight away; they're a draft until saved.
  const [draft, setDraft] = useState<ChampionsConfig | null>(null);
  const [save, setSave] = useState<SaveState>({ status: "idle" });
  const config = draft ?? remote.config;
  const [adminOpen, setAdminOpen] = useState(false);
  const people = useChampionDirectory(config.champions);
  const { communities, milestones } = config;
  const { profile } = useProfile({ skipPhoto: true });
  const me = (profile?.mail ?? profile?.userPrincipalName ?? "").toLowerCase() || null;
  // ?community=tech opens that community straight away (the dashboard links here).
  const [params] = useSearchParams();
  const [open, setOpen] = useState<{ id: CommunityId; highlight: string | null } | null>(() => {
    const id = params.get("community");
    return DEFAULT_CONFIG.communities.some((c) => c.id === id) ? { id: id as CommunityId, highlight: null } : null;
  });
  const [query, setQuery] = useState("");
  const close = useCallback(() => setOpen(null), []);
  const closeAdmin = useCallback(() => setAdminOpen(false), []);
  const [guide, setGuide] = useState(false);
  const closeGuide = useCallback(() => setGuide(false), []);

  // The dashboard links to #champions-cup; the router doesn't scroll to it.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [hash]);

  const saveDraft = async (photos: Map<string, File>) => {
    if (!draft || !remote.loaded) return;
    setSave({ status: "saving", message: "Saving…" });
    try {
      await saveChampions(await getToken(), remote.loaded, draft, photos, (message) => setSave({ status: "saving", message }));
      await remote.reload();
      setDraft(null);
      setSave({ status: "saved" });
    } catch (err) {
      // Some rows may have saved: reload so the next save starts from what's there.
      await remote.reload();
      setSave({ status: "error", message: err instanceof Error ? err.message : String(err) });
    }
  };

  const byCommunity = useMemo(() => {
    const map = {} as Record<CommunityId, ChampionProfile[]>;
    for (const c of communities) map[c.id] = people.filter((p) => p.community === c.id);
    return map;
  }, [people, communities]);

  const largest = Math.max(...communities.map((c) => byCommunity[c.id].length));
  const myCommunity = people.find((p) => me && p.email?.toLowerCase() === me)?.community ?? null;
  const faces = people.filter((p) => p.photoUrl).slice(0, 12);
  const leads = config.leads.map((n) => people.find((p) => p.name === n)).filter(Boolean) as ChampionProfile[];

  const q = query.trim().toLowerCase();
  const matches = q
    ? people.filter((p) =>
        [p.name, p.title, p.area, p.location].some((v) => v?.toLowerCase().includes(q))
      )
    : [];

  const today = todayYmd();

  return (
    <div className="page db ch">
      <section className="ch-hero" aria-labelledby="ch-title">
        <button type="button" className="ch-admin-btn" onClick={() => setAdminOpen(true)}>
          <Settings2 size={15} aria-hidden /> Champion admin
          {draft ? <span className="ch-admin-dot" title="Unsaved changes" /> : null}
        </button>
        <div className="ch-hero-text">
          <p className="ch-hero-eyebrow">AI Champions program</p>
          <h1 id="ch-title" className="ch-hero-title">
            {config.headline}
          </h1>
          <p className="ch-hero-lede">{config.lede}</p>
          <div className="ch-hero-actions">
            <a className="db-hero-btn db-hero-btn--primary" href="#ch-communities">
              Find your champion <ArrowRight size={15} aria-hidden />
            </a>
            <a className="db-hero-btn" href={config.surveyUrl} target="_blank" rel="noreferrer">
              Take the AI Journey survey <ExternalLink size={14} aria-hidden />
            </a>
          </div>
          <dl className="ch-kpis">
            <div>
              <dt>AI champions</dt>
              <dd>{people.length}</dd>
            </div>
            <div>
              <dt>Communities</dt>
              <dd>{communities.length}</dd>
            </div>
            <div>
              <dt>In largest community</dt>
              <dd>{largest}</dd>
            </div>
          </dl>
        </div>
        <div className="ch-mosaic" aria-hidden>
          {faces.map((p) => (
            <Face key={p.name} name={p.name} photoUrl={p.photoUrl} size={58} />
          ))}
        </div>
      </section>

      <section className="db-card ch-what" aria-label="What champions do">
        {[
          { icon: CalendarClock, title: "Office hours", text: "Regular sessions built around real work from your team." },
          { icon: Sparkles, title: "Tips and workflows", text: "Practical prompts and workflows you can reuse right away." },
          { icon: Compass, title: "A way in", text: "Connections to approved AI tools, training and support." },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title} className="ch-what-item">
            <span className="ch-what-icon" aria-hidden>
              <Icon size={18} />
            </span>
            <div>
              <p className="ch-what-title">{title}</p>
              <p className="ch-what-text">{text}</p>
            </div>
          </div>
        ))}
      </section>

      <section id="ch-communities" className="db-card" aria-labelledby="ch-comm-title">
        <div className="db-section-head">
          <div>
            <h2 id="ch-comm-title" className="db-section-title">
              Find your community
            </h2>
            <p className="db-section-sub">Communities of practice, each with its own champions. Pick one to meet them.</p>
          </div>
          <label className="ch-search">
            <Search size={15} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, role or city"
              aria-label="Search champions"
            />
          </label>
        </div>

        {q ? (
          matches.length ? (
            <ul className="ch-results">
              {matches.map((p) => (
                <li key={p.name}>
                  <button
                    type="button"
                    className="ch-result"
                    onClick={() =>
                      setOpen({ id: p.community, highlight: p.name.replace(/\W+/g, "-").toLowerCase() })
                    }
                  >
                    <Face name={p.name} photoUrl={p.photoUrl} size={36} />
                    <span className="ch-result-text">
                      <span className="ch-result-name">{p.name}</span>
                      <span className="ch-result-meta">
                        {[p.title, communities.find((c) => c.id === p.community)?.name].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <ArrowRight size={15} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="db-empty-text">No champions match “{query}”.</p>
          )
        ) : (
          <ul className="ch-grid">
            {communities.map((c) => {
              const Icon = COMMUNITY_ICONS[c.id];
              const members = byCommunity[c.id];
              const shown = members.slice(0, 5);
              const next = timelineFor(milestones, c.id).find((m) => m.date >= today);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`ch-comm ch-t-${c.id}`}
                    onClick={() => setOpen({ id: c.id, highlight: null })}
                    aria-label={`${c.name}: ${members.length} champions`}
                  >
                    <span className="ch-comm-top">
                      <span className="ch-tile" aria-hidden>
                        <Icon size={18} />
                      </span>
                      {c.id === myCommunity ? <span className="ch-you">Your community</span> : null}
                    </span>
                    <span className="ch-comm-name">{c.name}</span>
                    <span className="ch-comm-areas">{unique(members.map((m) => m.area)).slice(0, 3).join(" · ")}</span>
                    <span className="ch-comm-foot">
                      <span className="ch-stack" aria-hidden>
                        {shown.map((p) => (
                          <Face key={p.name} name={p.name} photoUrl={p.photoUrl} size={30} />
                        ))}
                        {members.length > shown.length ? (
                          <span className="ch-face ch-face--more">+{members.length - shown.length}</span>
                        ) : null}
                      </span>
                      <span className="ch-comm-count">{members.length} champions</span>
                    </span>
                    {next ? (
                      <span className="ch-comm-next">
                        <CalendarClock size={13} aria-hidden />
                        <span>
                          <b>{fmtDate(next.date)}</b> · {next.text}
                        </span>
                      </span>
                    ) : null}
                    <span className="ch-comm-go">
                      Meet the champions <ArrowRight size={14} aria-hidden />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="db-card ch-join" aria-labelledby="ch-join-title">
        <div>
          <p className="ch-kicker">Join the program</p>
          <h2 id="ch-join-title" className="ch-join-title">
            Want to become an AI Champion?
          </h2>
          <p className="ch-join-text">
            New champions are always welcome as we build an AI-fluent culture. Reach out to one of the program leads.
          </p>
        </div>
        <ul className="ch-leads">
          {leads.map((p) => (
            <li key={p.name}>
              <a
                className="ch-lead"
                href={`mailto:${p.email}?subject=${encodeURIComponent("Becoming an AI Champion")}`}
              >
                <Face name={p.name} photoUrl={p.photoUrl} size={36} />
                <span>
                  <span className="ch-lead-name">{p.name}</span>
                  <span className="ch-lead-title">{p.title}</span>
                </span>
                <Mail size={15} aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      </section>

      <ChampionsCup
        communities={communities}
        exclude={config.leaderboardExclude}
        mine={myCommunity}
        onHowPoints={() => setGuide(true)}
      />

      <TopContributors champions={people} viewerEmail={me} exclude={config.leaderboardExclude} />
      {guide ? <PointsGuide onClose={closeGuide} /> : null}

      {open ? (
        <CommunityModal
          community={communities.find((c) => c.id === open.id)!}
          people={byCommunity[open.id]}
          milestones={timelineFor(milestones, open.id)}
          highlight={open.highlight}
          me={me}
          onClose={close}
        />
      ) : null}
      {adminOpen ? (
        <ChampionAdmin
          config={config}
          people={people}
          dirty={draft !== null}
          canSave={remote.loaded !== null}
          perCommunityTimeline={remote.loaded?.milestoneCommunities ?? false}
          save={save}
          onChange={(c) => {
            setDraft(c);
            if (save.status !== "saving") setSave({ status: "idle" });
          }}
          onSave={saveDraft}
          onDiscard={() => {
            setDraft(null);
            setSave({ status: "idle" });
          }}
          onClose={closeAdmin}
        />
      ) : null}
    </div>
  );
}
